// Tenantra Inc. - Screening link controls for one address (turn on, fee, copy) + short pointer in the property panel
import { useEffect, useState } from 'react';
import { Copy, ExternalLink, Loader2, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { navigate } from '@/services/navigation';
import api, { apiHelpers } from '@/services/api';
import { Section, copyText } from './DetailPanel';

const linkUrl = (token) => `${window.location.origin}/apply/${token}`;

export function ScreeningLinkControls({ propertyId, verified, onChange, compact = false }) {
  const [link, setLink] = useState(null);
  const [fee, setFee] = useState('0');
  const [rent, setRent] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    setLink(null);
    api.get(`/screening/properties/${propertyId}/link`).then(r => { if (live) { setLink(r.data); setFee(String(r.data.application_fee || 0)); setRent(r.data.monthly_rent != null ? String(r.data.monthly_rent) : ''); } })
      .catch(e => live && toast.error(apiHelpers.handleError(e)));
    return () => { live = false; };
  }, [propertyId]);

  const save = async (active, feeOnly = false) => {
    if (active && (rent === '' || !(Number(rent) > 0))) { toast.error('Enter the monthly asking rent before going live — it drives affordability scoring'); document.querySelector('[data-testid=screening-rent-input]')?.focus(); return; }
    if (fee !== '' && (!/^\d+(\.\d{1,2})?$/.test(fee) || Number(fee) > 100)) { toast.error('Enter a fee between $0 and $100'); return; }
    if (rent !== '' && (!/^\d+(\.\d{1,2})?$/.test(rent) || Number(rent) > 100000)) { toast.error('Enter a valid monthly rent'); return; }
    setBusy(true);
    try {
      const r = await api.put(`/screening/properties/${propertyId}/link`, { active, application_fee: Number(fee) || 0, monthly_rent: rent === '' ? null : Number(rent) });
      setLink(r.data); onChange?.(r.data);
      toast.success(feeOnly ? 'Listing saved' : active ? 'Screening link is live' : 'Screening link turned off');
    } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); }
  };

  if (!link) return <div className={`${compact ? 'h-14' : 'h-8'} bg-slate-100 rounded animate-pulse`} />;
  if (!verified) return <p className="text-sm text-slate-500" data-testid="screening-link-locked">Verify ownership of this address to start taking applications.</p>;
  return (
    <div className={`min-w-0 flex flex-col ${compact ? 'gap-3' : 'lg:flex-row lg:items-center gap-3 lg:gap-5'}`} data-testid="screening-link-section">
      <div className="flex items-center gap-2.5 shrink-0">
        <Switch checked={link.active} disabled={busy} onCheckedChange={(v) => save(v)} data-testid="screening-link-toggle" />
        <div><p className="text-sm text-slate-800">{link.active ? 'Taking applications' : 'Not taking applications'}</p>
          {compact && <p className="text-xs text-slate-500">One link for this address</p>}</div>
      </div>
      <div className={`flex items-center gap-2 shrink-0 ${compact ? '' : 'lg:border-l lg:border-slate-100 lg:pl-5'}`}>
        <span className="text-xs text-slate-500">Rent <span className="text-red-500">*</span></span>
        <div className="flex items-center gap-1"><span className="text-slate-400 text-xs">$</span>
          <Input value={rent} inputMode="decimal" placeholder="—" aria-label="Monthly asking rent (CAD)" onChange={(e) => setRent(e.target.value.replace(/[^\d.]/g, ''))} className={`h-7 w-20 text-xs bg-white ${link.active && !rent ? 'border-red-300 ring-1 ring-red-200' : ''}`} data-testid="screening-rent-input" /><span className="text-slate-400 text-[11px]">/mo</span></div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs text-slate-500">Fee</span>
        <div className="flex items-center gap-1"><span className="text-slate-400 text-xs">$</span>
          <Input value={fee} inputMode="decimal" aria-label="Application fee (CAD)" onChange={(e) => setFee(e.target.value.replace(/[^\d.]/g, ''))} className="h-7 w-16 text-xs bg-white" data-testid="screening-fee-input" /></div>
        <button onClick={() => save(link.active, true)} disabled={busy} className="h-7 px-2.5 rounded-md border border-slate-200 bg-white text-xs text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center gap-1" data-testid="screening-fee-save">{busy && <Loader2 className="w-3 h-3 animate-spin" />} Save</button>
      </div>
      {link.active && link.token && (
        <div className="flex-1 min-w-0 flex items-center gap-2 border border-slate-200 rounded-md pl-3 pr-1 py-0.5 bg-slate-50/60">
          <span className="flex-1 min-w-0 truncate text-xs font-mono text-slate-700" data-testid="screening-link-url">{linkUrl(link.token)}</span>
          <button onClick={() => copyText(linkUrl(link.token))} className="h-7 px-2 rounded text-xs font-medium text-teal-700 hover:bg-teal-50 inline-flex items-center gap-1" data-testid="screening-link-copy"><Copy className="w-3.5 h-3.5" /> Copy</button>
          <a href={linkUrl(link.token)} target="_blank" rel="noreferrer" className="h-7 w-7 rounded inline-flex items-center justify-center text-slate-500 hover:text-teal-700 hover:bg-teal-50" aria-label="Open application form" data-testid="screening-link-open"><ExternalLink className="w-3.5 h-3.5" /></a>
        </div>
      )}
    </div>
  );
}

export function ScreeningLinkSection({ property }) {
  const [link, setLink] = useState(null);
  useEffect(() => { api.get(`/screening/properties/${property.id}/link`).then(r => setLink(r.data)).catch(() => setLink({})); }, [property.id]);
  return (
    <Section title="Screening" aside={link?.applications_count ? `${link.applications_count} applicant${link.applications_count === 1 ? '' : 's'}` : ''}>
      <div className="flex items-center justify-between gap-3" data-testid="screening-link-summary">
        <p className="text-sm text-slate-600">{!link ? 'Loading…' : link.active ? 'Taking applications' : 'Not taking applications'}</p>
        <button onClick={() => navigate(`/landlord/screening?property=${property.id}`)} className="text-xs font-medium text-teal-700 hover:text-teal-800 inline-flex items-center gap-1" data-testid="screening-manage-link">Manage in Screening <ArrowUpRight className="w-3.5 h-3.5" /></button>
      </div>
    </Section>
  );
}
